import { ReactComponent as ThumbsDownIcon } from 'assets/images/feedbacks/ic-thumbs-down.svg'
import { ReactComponent as ThumbsUpIcon } from 'assets/images/feedbacks/ic-thumbs-up.svg'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('components.feedback_rating')

export type FeedbackRating = 'up' | 'down'

// Pinned width of the Response (thumb) column. Shared by every table that
// renders one — FeedbackTable (Details tab / RAI Escalations) and
// RecentActivitiesTable (order-rating detail / RAI case detail) — so the
// centered header and the centered glyph land at the same offset on all of
// them. Without a pinned width an auto-layout table hands the column its share
// of the leftover width and the glyph drifts away from the header.
export const RESPONSE_COLUMN_WIDTH_PX = 112

const ICON_SIZE_PX = 20

type Props = {
  rating: FeedbackRating
  // Defaults to the Response-column size shared by both tables. Callers with
  // a different pinned size (e.g. InboxListRow's smaller row avatar) can
  // override it rather than introducing a third hardcoded size of their own.
  size?: number
}

// The thumbs up/down glyph rendered in the Response column of both feedback
// tables (and, at a smaller size, the Unified Feedback Inbox's list row
// avatar). Extracted so callers never disagree on size, colour, or
// accessible name. The labels describe the RATING, not the column, so they
// still read "feedback" even though the column header reads "Response".
const FeedbackRatingIcon = ({ rating, size = ICON_SIZE_PX }: Props) => {
  const [css] = useStyletron()

  const iconClass = css({
    width: `${size}px`,
    height: `${size}px`,
  })

  if (rating === 'up') {
    return (
      <ThumbsUpIcon
        className={`${iconClass} ${css({
          // palette for both glyphs, not merchantTheme for one and palette for
          // the other — the two call sites this was extracted from disagreed.
          // palette.nephritis is the same #27AE60 merchantTheme.colors
          // .nephritis50 resolved to, so this is a source change, not a colour
          // change. CLAUDE.md points css({}) styles at palette constants.
          color: palette.nephritis,
        })}`}
        role="img"
        aria-label={t('positive')}
      />
    )
  }

  return (
    <ThumbsDownIcon
      className={`${iconClass} ${css({
        color: palette.red50,
      })}`}
      role="img"
      aria-label={t('negative')}
    />
  )
}

export default FeedbackRatingIcon
